/* eslint-disable no-console */
import React, { useRef, useState } from 'react'

import { IConversationQuestionViewProps } from './types'
import { ChatMessage } from '../types'

import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import InputTextarea from '../../components/InputTextarea'

import style from './style.module.scss'
import commonStyle from '../_common/style.module.scss'
import ImageAsset from '@aytm/app/swidget/base/modules/images/ImageAsset.tsx'
import useViewport from '../../modules/viewport/useViewport'
import useWebcam from '@aytm/app/swidget/base/modules/webcam/useWebcam.ts'
import WebcamModule from '../../modules/webcam/Module'
import { AudioVisualizer, LiveAudioVisualizer } from 'react-audio-visualize'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import ClientModule from '@aytm/app/swidget/base/modules/client/Module.ts'
import { IAssetResponse } from '../../modules/client/types'
import useTheme from '../../modules/theme/useTheme'
import Timer from '@aytm/app/swidget/base/questions/_common/Timer.tsx'
import { hapticEffects } from '../../lib/hapticEffects'

interface IInputBlockProps {
  valid: boolean
  disabled: boolean
  showValidation: boolean
  audioMode: boolean
  cantRecord: boolean
  onlyAudio: boolean
  onlyText?: boolean
  error: boolean
  onCommentSend: (value: string) => void
  startRecord: () => void
}

const isSelect = (event: React.KeyboardEvent) => {
  const { key } = event
  if (key !== ' ' && key !== 'Enter') return false
  event.preventDefault()
  event.stopPropagation()
  return true
}

const InputBlock = ({
  valid,
  disabled,
  showValidation,
  onCommentSend,
  startRecord,
  onlyAudio,
  onlyText,
  cantRecord,
  audioMode,
  error
}: IInputBlockProps) => {
  const [value, setValue] = useState('')
  const cantStartAudio = value.length || !startRecord || valid
  const inputRef = useRef(null)
  // const session = useSession()

  const recordStart = () => {
    if (!cantStartAudio) startRecord()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    if (inputRef.current) inputRef.current?.querySelector('textarea')?.focus()
  }

  const onCommentChange = (value: string) => {
    setValue(value)
  }

  const sendComment = (value: string, event?: React.MouseEvent) => {
    if ((!disabled || error) && value.length) {
      if (event) {
        const tag = (event.target as HTMLElement).tagName
        if (('BUTTON' === tag || 'I' === tag) && inputRef.current) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          inputRef.current?.querySelector('textarea')?.focus()
        }
      }
      onCommentSend(value)
      setValue('')
    }
  }
  const onKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ('Enter' == event.key && !event.shiftKey) {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      sendComment(value)
    }
  }
  const { t } = useSurveyLocale()
  return (
    <div className={style.inputBlock} ref={inputRef}>
      <InputTextarea
        className={`${style.textarea} ${showValidation && !valid ? style.error : ''}`}
        placeholder={t(
          'questions',
          'cn',
          onlyAudio ? 'placeholderAudio' : onlyText ? 'placeholderText' : 'placeholder'
        )}
        rows={3}
        autoResize={true}
        autoFocus={true}
        disabled={valid || onlyAudio}
        debounce={0}
        value={value}
        data-testid="SW-CN-Input"
        onInputChange={onCommentChange}
        onKeyPress={onKeyPress}
        onFocus={() => hapticEffects.soft()}
      />

      <button
        aria-label={
          value.length || (!startRecord && !value.length && !cantRecord)
            ? t('questions', 'cn', 'sendMessage')
            : t('questions', 'cn', 'startRecording')
        }
        onClick={
          value.length || (!startRecord && !value.length && !cantRecord)
            ? event => sendComment(value, event)
            : recordStart
        }
        disabled={
          value.length || (!startRecord && !value.length && !cantRecord)
            ? (disabled || valid || audioMode || !value.length) && !error
            : (disabled && !error) || cantRecord
        }
      >
        {value.length || (!startRecord && !value.length && !cantRecord) ? (
          <i
            className={`${(disabled || valid || audioMode || !value.length) && !error ? style.disabled : ''}  ${
              showValidation && !valid && disabled ? style.error : ''
            } icon-aytmfd-qconversation-send`}
          />
        ) : (
          <>
            <i
              className={` ${
                (showValidation && !valid) || cantRecord ? 'icon-aytmfd-qconversation-nomic' : 'icon-aytmfd-microphone'
              } ${style.microphone} ${style.start} ${(disabled && !error) || cantRecord ? style.disabled : ''}  `}
            />
          </>
        )}
      </button>
      {/*{startRecord  ? (*/}
      {/*  <i*/}
      {/*    className={` ${showValidation && !valid ? style.error : ''} ${style.microphone} ${*/}
      {/*      style.start*/}
      {/*    } icon-aytmfd-microphone`}*/}
      {/*    onClick={cantStartAudio ? null : startRecord}*/}
      {/*  />*/}
      {/*) : (*/}
      {/*  ''*/}
      {/*)}*/}
    </div>
  )
}

interface IAudioBlockProps {
  onCommentSend: (value: string, blob?: Blob, token?: string, error?: boolean) => void
  onAudioDelete: () => void
  onStartSending: (blob?: Blob) => void
  valid: boolean
}

const AudioBlock = ({ onCommentSend, onStartSending, onAudioDelete, valid }: IAudioBlockProps) => {
  const [recording, setRecording] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [sending, setSending] = useState(false)
  const [blob, setBlob] = useState<Blob>(null)
  const started = useRef(false)
  const clicked = useRef(false)
  const WebCam = useWebcam(false, true, false)
  const viewport = useViewport()
  const session = useSession()
  const { t } = useSurveyLocale()

  const startRecord = () => {
    if (!sending && !valid) {
      WebCam.startRecording()
      setRecording(true)
      setTimeout(() => setEnabled(true), 200)
    }
  }

  if (!started.current && !recording && WebCam.ready && WebCam.stream.active) {
    started.current = true
    startRecord()
  }

  const clearAudio = () => {
    setBlob(null)
    onAudioDelete()
  }

  const sendAudio = () => {
    setSending(true)
    WebCam.stopRecording()
    setRecording(false)
    setEnabled(false)
    setBlob(WebCam.blobs)
    onStartSending(WebCam.blobs)
    clicked.current = true
    if (WebCam.blobs) {
      clicked.current = false
      const uploadUrl = session.urls?.upload_audio_response_asset
      const file = new File([WebCam.blobs], 'message.mp4', {
        type: WebCam.blobs.type
      })
      ClientModule.sendFile(file, uploadUrl, session.action.action_proxy_params)
        .then(asset => {
          // eslint-disable-next-line no-console
          setSending(false)
          const assetResponse = asset as IAssetResponse
          if ('error' == assetResponse.status) {
            if ('timeout' == assetResponse.text) {
              clearAudio()
            }
            onCommentSend(assetResponse.audio_asset_token, null, null, true)
          } else {
            onCommentSend(assetResponse.text, WebCam.blobs, assetResponse.audio_asset_token)
            setBlob(null)
            // clearAudio()
          }
        })
        .catch(error => {
          setSending(false)
          onCommentSend('', null, null, true)
          // eslint-disable-next-line no-console
          console.log(error)
        })
    }
  }

  WebcamModule.onUpdate(() => {
    if (!started.current && !recording && WebCam.ready && WebCam.stream.active) {
      started.current = true
      startRecord()
    }
    if (!blob && WebCam.blobs && sending && clicked.current) {
      setBlob(WebCam.blobs)
      sendAudio()
    }
  })

  return (
    <div className={style.inputBlock}>
      <div className={style.frame}>
        {recording ? (
          <>
            {WebcamModule.recorder ? (
              <>
                <LiveAudioVisualizer
                  mediaRecorder={WebcamModule.recorder || null}
                  width={(viewport.widgetWidth as number) - 100}
                  height={89}
                  maxDecibels={100}
                  minDecibels={viewport.mobile ? -150 : -120}
                  fftSize={viewport.mobile ? 64 : 1024}
                  barWidth={5}
                  gap={3}
                  smoothingTimeConstant={0.9}
                />
                <span className={style.timer}>
                  <Timer warn={45} max={60} onEnd={sendAudio} />
                </span>
              </>
            ) : (
              ''
            )}
          </>
        ) : (
          ''
        )}

        {!sending && recording && (
          <i
            tabIndex={0}
            role="button"
            aria-label={t('questions', 'cn', 'delete')}
            className={`${style.delete} icon-aytmfd-close`}
            onClick={clearAudio}
            onKeyDown={event => isSelect(event) && clearAudio()}
          />
        )}

        {!sending ? (
          <i
            tabIndex={0}
            role="button"
            className={`icon-aytmfd-qconversation-send ${!enabled ? style.disabled : ''} ${style.start}`}
            onClick={() => sendAudio()}
            onKeyDown={event => isSelect(event) && sendAudio()}
          />
        ) : (
          <i
            tabIndex={0}
            role="button"
            className={` ${style.microphone} ${sending || valid || !recording ? style.disabled : ''}  ${
              style.start
            } ${'icon-aytmfd-microphone'}`}
            onClick={recording ? sendAudio : startRecord}
            onKeyDown={event => (isSelect(event) && recording ? sendAudio : startRecord)}
          />
        )}
      </div>
    </div>
  )
}
const ConversationView = ({
  messages,
  voices,
  schema,
  responseValidation,
  resetValidation,
  showValidation,
  sendMessage,
  onUpdateResponse,
  error
}: IConversationQuestionViewProps) => {
  const currentQuestionComponentRef = useRef(null)
  const [lastMessage, setLastMessage] = useState('')

  const [sending, setSending] = useState(true)
  const [innerError, setInnerError] = useState(false)
  const [disabledCamera, setDisabledCamera] = useState(false)
  const list = useRef<ChatMessage[]>([])
  const viewport = useViewport()
  const [audioMode, setAudioMode] = useState(false)
  const [blob, setBlob] = useState<Blob>(null)
  const [time, setTime] = useState(0)
  const [played, setPlayed] = useState<number>(null)
  const [paused, setPaused] = useState(false)
  const startCamera = useRef(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const theme = useTheme()
  const { t } = useSurveyLocale()
  const currentMode = useRef(false)

  if (currentMode.current != audioMode) {
    if (audioMode && !currentMode.current) {
      setPlayed(null)
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    currentMode.current = audioMode
  }

  if (!startCamera.current) {
    startCamera.current = true
  }

  // WebcamModule.onUpdate(() => {
  //   setCamera(WebcamModule.ready)
  // })

  const onStartSending = (blob?: Blob) => {
    if (innerError) setInnerError(false)
    setSending(true)
    setBlob(blob)
  }

  const onStopSending = () => {
    setLastMessage('')
    setSending(false)
    setBlob(null)
  }

  if (sending && list.current[list.current.length - 1]?.status != messages[messages.length - 1]?.status) {
    list.current = messages
    onStopSending()
  }

  // console.log(messages.at(-1)?.text)

  const startRecord = () => {
    setInnerError(false)
    if (WebcamModule && !WebcamModule.ready)
      WebcamModule.init(true, false)
        .then(() => {
          if (WebcamModule.ready) setAudioMode(true)
          else {
            setDisabledCamera(true)
          }
        })
        .catch(error => {
          console.log(error)
        })

    if (WebcamModule && WebcamModule.ready) setAudioMode(true)
  }

  const onCommentSend = (value: string, blob?: Blob, token?: string, error?: boolean) => {
    if (null != error) {
      setInnerError(error)
      setSending(false)
    }
    setAudioMode(false)
    if (error) return
    setInnerError(false)
    list.current = messages
    setLastMessage(value)
    sendMessage(value, blob, token, onStopSending)
    setSending(true)
  }

  const onAudioDelete = () => {
    setAudioMode(false)
    setInnerError(false)
    resetValidation()
    // onStartSending(blob)
  }

  const playAudio = (i: number) => {
    if (!i) return
    if (i == messages.length && !blob) return
    if (played == i) {
      if (paused) void audioRef.current.play()
      else audioRef.current.pause()
      setPaused(!paused)
      return
    }
    const reader = new FileReader()
    setPlayed(i)
    setPaused(false)
    reader.onload = event => {
      const srcUrl = event.target.result
      audioRef.current.src = srcUrl as string
      void audioRef.current.play()
    }
    reader.readAsDataURL(blob && i == messages.length ? blob : voices[i])
    audioRef.current.load()
    audioRef.current.onload = () => {
      void audioRef.current.play()
    }
  }

  const onTimeUpdate = () => {
    // console.log(i)
    // console.log(audioRef.current.currentTime)
    setTime(audioRef.current.currentTime)
    // setPrc(a)
  }

  const audioEnded = () => {
    setPlayed(null)
  }

  const skip = () => {
    onUpdateResponse({ skip: true })
  }

  return (
    <section className={style.conversationView} aria-labelledby={`questionTitle-${schema.id}`} data-testid="SW-CN">
      <h2 id={`questionTitle-${schema.id}`} className={commonStyle.srOnly}>
        {schema.text}
      </h2>
      <audio ref={audioRef} onTimeUpdate={onTimeUpdate} onEnded={audioEnded} />
      <div className={`${style.chatWindow} ${viewport.mobile ? style.mobile : ''}`} ref={currentQuestionComponentRef}>
        {sending && !messages.length ? (
          <div className={style.overlay}>
            <div aria-hidden="true" className={`${commonStyle.spinner} ${style.spinner}`} />
          </div>
        ) : (
          ''
        )}
        {innerError && <span className={style.error}>{t('questions', 'cn', 'sorry')}</span>}
        {error && !sending && <span className={style.error}>{t('questions', 'ir', 'somethingWrong')}</span>}
        <div aria-label={t('question', 'cn', 'conversation')} role="region" aria-live="polite">
          {messages?.map((message, i) => {
            const answer = schema.answers.find(a => message.id == a.id)
            const firstMessage = message == messages.find(m => message.id == m.id)
            return (
              <div
                key={i}
                aria-details={'llm' == message.type ? 'skipper' : 'you'}
                className={`${style.message} ${'llm' != message.type ? style.gray : ''}`}
              >
                <i
                  className={`${style.icon} ${
                    'llm' == message.type ? 'icon-aytmfd-qconversation-skipper' : 'icon-aytmfd-qconversation-user'
                  }`}
                />

                <div className={style.text}>
                  <div className={style.title}>
                    <b>{t('questions', 'cn', 'llm' == message.type ? 'skipper' : 'you')} </b>
                    {'next_message_job' == message.status && !error && !innerError ? (
                      <div className={style.typing}>{t('questions', 'cn', 'typing')}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  {voices[i] ? (
                    <div className={style.audioMessage}>
                      {/*<div onClick={played == i ? rewind : null}>*/}
                      <AudioVisualizer
                        blob={voices[i]}
                        width={viewport.mobile ? viewport.width - 100 : 300}
                        height={89}
                        barWidth={5}
                        gap={3}
                        barColor={'#b2b2b2'}
                        barPlayedColor={theme.style.accentColor}
                        currentTime={played == i ? time : null}
                      />
                      <i
                        tabIndex={0}
                        role="button"
                        aria-label={
                          played == i && !paused ? t('questions', 'cn', 'stopPlay') : t('questions', 'cn', 'startPlay')
                        }
                        className={`${style.icon} ${
                          played == i && !paused ? 'icon-aytmfd-pause' : 'icon-aytmfd-video-play'
                        }`}
                        onClick={() => playAudio(i)}
                        onKeyDown={event => isSelect(event) && playAudio(i)}
                      />
                    </div>
                  ) : (
                    ''
                  )}
                  <>
                    {answer?.image && firstMessage && 'next_message_job' != message.status ? (
                      <ImageAsset asset={answer?.image || null} size={'medium'} className={style.image} />
                    ) : (
                      ''
                    )}
                    {message.text ? (
                      <span
                        className={`${voices[i] ? style.small : ''}`}
                        dangerouslySetInnerHTML={{ __html: message.text }}
                      ></span>
                    ) : (
                      ''
                    )}
                  </>
                  {'retry' == message.status ? (
                    <a onClick={() => onCommentSend('')}>{t('questions', 'ir', 'somethingWrong')}</a>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            )
          })}

          {sending && blob ? (
            <>
              <div key="last" className={`${style.message} ${style.gray}`}>
                <i className={`${style.icon} icon-aytmfd-qconversation-user`} />
                <div className={style.text}>
                  <div className={style.title}>
                    <b>You</b>
                  </div>
                  <div className={style.audioMessage}>
                    <AudioVisualizer
                      blob={blob}
                      width={viewport.mobile ? viewport.width - 100 : 300}
                      height={89}
                      barWidth={5}
                      gap={3}
                      barColor={'#b2b2b2'}
                      barPlayedColor={theme.style.accentColor}
                      currentTime={played == messages.length ? time : null}
                    />
                    <i
                      tabIndex={0}
                      role="button"
                      className={`${style.icon} ${
                        played == messages.length && !paused ? 'icon-aytmfd-pause' : 'icon-aytmfd-video-play'
                      }`}
                      onClick={() => playAudio(messages.length)}
                    />
                  </div>
                  {lastMessage ? (
                    <span className={style.small} dangerouslySetInnerHTML={{ __html: lastMessage }}></span>
                  ) : (
                    ''
                  )}
                </div>
              </div>
              <div className={style.message}>
                <i className={`${style.icon} ${'icon-aytmfd-qconversation-skipper'}`} />
                <div className={style.text}>
                  <div className={style.title}>
                    <b>Skipper </b>
                    <div className={style.typing}>{t('questions', 'cn', 'typing')}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            ''
          )}
        </div>
      </div>
      {audioMode && 'text' != schema.mode ? (
        <AudioBlock
          onCommentSend={onCommentSend}
          onAudioDelete={onAudioDelete}
          onStartSending={onStartSending}
          valid={responseValidation.done}
        />
      ) : (
        <InputBlock
          disabled={sending || 'request' != messages[messages.length - 1]?.status}
          valid={responseValidation.done}
          audioMode={audioMode}
          error={error}
          onlyAudio={'audio' == schema.mode}
          onlyText={'text' == schema.mode}
          cantRecord={disabledCamera}
          onCommentSend={onCommentSend}
          showValidation={showValidation}
          startRecord={disabledCamera || 'text' == schema.mode ? null : startRecord}
        />
      )}

      {messages.length < 2 ? (
        <button className={style.skip} onClick={skip}>
          {t('questions', 'vr', 'skip')}
        </button>
      ) : (
        ''
      )}
    </section>
  )
}

export default ConversationView
